import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, useMediaQuery } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import {
  AiSparkleIcon,
  DocLinkIcon,
  DocumentIcon,
  InfoIcon,
} from '@sharedCR/coreDesignSystem/icons';
import IntakeStatusCard from 'app/pages/loanApplication/tabs/aiActivity/IntakeStatusCard';
import storage from 'app/services/storage';
import DocumentView from 'app/shared/components/document/DocumentView';
import { WorksheetDrawer } from 'app/shared/modules/DocumentWorksheet';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import withPagePermissions from 'hocs/withPagePermissions';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { BlockSizesLocalStorageKey } from 'constants/localStorage';
import { PAGE_CODES } from 'constants/pageCodes';
import useContentWidth from 'hooks/useContentWidth';
import { useDocumentActions } from 'hooks/useDocumentActions';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePopover from 'hooks/usePopover';
import usePrevious from 'hooks/usePrevious';
import useUrlSearchParams from 'hooks/useUrlSearchParams';
import { checkIsRecipientCopy } from 'utils/documents';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage, selectIsAiDocumentIntakeAllowed } from 'store/auth/selectors';
import { initIntakeForLoanAction } from 'store/documentIntake';
import { getDocumentsAction } from 'store/documents/actions';
import {
  getDocumentExtractionFields,
  getDocuments,
  selectDocumentTotalLinkCount,
} from 'store/documents/selectors';
import { getInspectorExtractionFieldsByDocTypeIdAction } from 'store/system/actions/documentExtractionFields.actions';
import { selectInspectorExtractionFieldsByDocTypeId } from 'store/system/selectors/documentExtractionFields.selectors';

import AiBatchIntake from './AiBatchIntake';
import CollapsedPaneRail from './CollapsedPaneRail';
import DocumentContextMenu from './DocumentContextMenu';
import DocumentInspector from './DocumentInspector';
import {
  INSPECTOR_TABS_CONFIG,
  InspectorTab,
} from './DocumentInspector/DocumentInspector.constants';
import { getExtractionFieldLeaves } from './DocumentInspector/DocumentInspector.utils';
import { getTaskLinkCount } from './DocumentInspector/DocumentLinksTab/DocumentLinksTab.utils';
import DocumentTreeView from './DocumentTreeView';
import { LoanDocumentsTreeNodeType } from './LoanApplicationDocuments.constants';
import {
  documentsRedesignLayoutStyles,
  inspectorStackingStyles,
  viewerPaneRedesignInlineClasses,
} from './LoanApplicationDocuments.styles';
import { LoanApplicationDocumentsTestIds } from './LoanApplicationDocuments.testIds';

const INSPECTOR_RAIL_TABS = [
  { key: InspectorTab.AI_FIELDS, icon: AiSparkleIcon },
  { key: InspectorTab.LINKS, icon: DocLinkIcon },
  { key: InspectorTab.DETAILS, icon: InfoIcon },
];

const Documents = ({
  loanParams,
  documentOwnerParams,
  pageCode = PAGE_CODES.DOCUMENTS,
  isDataLoaded = false,
  documentWrapperClassName = 'h-full',
  setIsLoading,
  isExtractedFieldsViewNeeded = false,
  isLoading,
  isCopyDocumentsAllowed = false,
  isEmbeddedInPane = false,
}) => {
  // Generic owner-scoped params bag (loan id OR subcompany id). Falls back to
  // loanParams so the loan editor call sites keep working unchanged.
  const ownerParams = documentOwnerParams ?? loanParams;

  const loanApplicationId = ownerParams?.applicationId ?? null;

  const dispatch = useDispatch();

  const documents = useSelector(getDocuments);

  const { isEditAllowed: isDocumentsEditAllowed } = useSelector(
    getMasterPermissionsToPage(pageCode),
  );

  const isAiDocumentIntakeAllowed = useSelector(selectIsAiDocumentIntakeAllowed);

  const documentTreeRef = useRef();
  const documentViewRef = useRef();

  const isThumbnailsHidden = useMediaQuery(theme => theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  const [documentId, setDocumentId] = useState('');
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [boundingBoxPopUpInfo, setBoundingBoxPopUpInfo] = useState();
  // The extraction field the user clicked to locate in the document (inspector "AI Fields" tab).
  const [locatedFieldSource, setLocatedFieldSource] = useState(null);
  const [isAiBatchIntakeOpen, setIsAiBatchIntakeOpen] = useState(false);
  const [reviewJob, setReviewJob] = useState(null);
  const [worksheetDrawerState, setWorksheetDrawerState] = useState(null);
  const { anchorPosition, onSetAnchorPosition, onClearAnchorPosition } = usePopover();

  // Collapse state for the left (documents list) panel — persisted to localStorage.
  const [isListCollapsed, setIsListCollapsed] = useState(
    () => !!storage.getParsedItem(BlockSizesLocalStorageKey.DOCUMENTS_LIST_COLLAPSED),
  );

  // Collapse state for the right (inspector) panel — persisted to localStorage.
  const [isInspectorCollapsed, setIsInspectorCollapsed] = useState(
    () => !!storage.getParsedItem(BlockSizesLocalStorageKey.DOCUMENTS_INSPECTOR_COLLAPSED),
  );

  // Inspector active tab — lifted here so the collapsed rail can show tab icons.
  const [inspectorActiveTab, setInspectorActiveTab] = useState(InspectorTab.AI_FIELDS);

  // Focus mode is derived: both panels collapsed at once.
  const isFocusMode = isListCollapsed && isInspectorCollapsed;

  const onCollapseList = useCallback(() => {
    setIsListCollapsed(true);
    storage.setItem(BlockSizesLocalStorageKey.DOCUMENTS_LIST_COLLAPSED, true, true);
  }, []);

  const onExpandList = useCallback(() => {
    setIsListCollapsed(false);
    storage.setItem(BlockSizesLocalStorageKey.DOCUMENTS_LIST_COLLAPSED, false, true);
  }, []);

  const onCollapseInspector = useCallback(() => {
    setIsInspectorCollapsed(true);
    storage.setItem(BlockSizesLocalStorageKey.DOCUMENTS_INSPECTOR_COLLAPSED, true, true);
  }, []);

  const onExpandInspector = useCallback(() => {
    setIsInspectorCollapsed(false);
    storage.setItem(BlockSizesLocalStorageKey.DOCUMENTS_INSPECTOR_COLLAPSED, false, true);
  }, []);

  const onToggleFocus = useCallback(() => {
    const nextCollapsed = !isFocusMode;

    setIsListCollapsed(nextCollapsed);
    setIsInspectorCollapsed(nextCollapsed);
    storage.setItem(BlockSizesLocalStorageKey.DOCUMENTS_LIST_COLLAPSED, nextCollapsed, true);
    storage.setItem(BlockSizesLocalStorageKey.DOCUMENTS_INSPECTOR_COLLAPSED, nextCollapsed, true);
  }, [isFocusMode]);

  const onExpandInspectorToTab = useCallback(
    tabKey => {
      setInspectorActiveTab(tabKey);
      onExpandInspector();
    },
    [onExpandInspector],
  );

  const { onDownloadDocument, onPrintDocument, onOpenDocumentInNewTab } =
    useDocumentActions(documentId);

  const resetDocumentId = useCallback(() => setDocumentId(''), []);
  const onAiBatchIntakeComplete = useCallback(() => setIsAiBatchIntakeOpen(false), []);
  const onOpenAiBatchIntake = useCallback(() => setIsAiBatchIntakeOpen(true), []);
  const onCloseAiBatchIntake = useCallback(() => setIsAiBatchIntakeOpen(false), []);
  const onOpenIntakeReview = useCallback(job => setReviewJob(job ?? null), []);
  const onCloseIntakeReview = useCallback(() => setReviewJob(null), []);
  const onIntakeReviewComplete = useCallback(() => {
    setReviewJob(null);

    if (ownerParams) {
      dispatch(getDocumentsAction(ownerParams));
    }
  }, [dispatch, ownerParams]);

  const currentDocument = documents[documentId];
  const isDocumentViewControlVisible =
    isDocumentsEditAllowed && !currentDocument?.isDeleted && !checkIsRecipientCopy(currentDocument);

  const onOpenWorksheet = useCallback(() => {
    if (!currentDocument?.documentTypeId) {
      return;
    }

    setWorksheetDrawerState({
      documentTypeId: currentDocument.documentTypeId,
      worksheetDocumentId: currentDocument.id ?? null,
    });
    onClearAnchorPosition();
  }, [currentDocument, onClearAnchorPosition]);

  const onCloseWorksheet = useCallback(() => setWorksheetDrawerState(null), []);

  const prevDocumentId = usePrevious(documentId);

  const { documentId: documentIdSearchParam } = useUrlSearchParams();

  const { containerRef, contentWidth, setContentWidth, setRef } = useContentWidth();

  const isDocumentIdChanged = !!prevDocumentId && documentId !== prevDocumentId;

  const onOpenContextMenu = useCallback(
    e => {
      const { treeNodeType, id } = e.currentTarget.dataset;
      const isDocumentItemSelected = treeNodeType === LoanDocumentsTreeNodeType.DOCUMENT_ITEM;

      if (isDocumentItemSelected) {
        e.preventDefault();

        setDocumentId(id);

        return onSetAnchorPosition(e);
      }
    },
    [onSetAnchorPosition],
  );

  const onDocumentLoaded = useCallback(() => setIsDocumentLoaded(true), []);

  const onRotateDocumentWithSave = useCallback(e => {
    documentViewRef.current?.onRotateDocument(e)?.then(() => documentViewRef.current?.onSavePage());
  }, []);

  const onSplitPages = useCallback(() => {
    documentViewRef.current?.onSplit();
    onClearAnchorPosition();
  }, [onClearAnchorPosition]);

  const onMoveDocumentToType = useCallback(
    (__, option) => {
      if (!option?.documentTypeId) {
        return;
      }

      documentTreeRef.current?.onUpdateDocument({
        typeId: option.documentTypeId,
        documentId,
      });

      onClearAnchorPosition();
    },
    [documentId, onClearAnchorPosition],
  );

  const inspectorExtractionFields = useSelector(getDocumentExtractionFields(documentId));
  const inspectorFlatFields = useSelector(
    selectInspectorExtractionFieldsByDocTypeId(currentDocument?.documentTypeId ?? ''),
  );
  const inspectorLinkCount = useSelector(selectDocumentTotalLinkCount);
  const { isViewAllowed: isExtractedFieldsViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_DOCUMENT_EXTRACTION_FIELDS),
  );

  const inspectorFieldLeafCount = useMemo(
    () =>
      getExtractionFieldLeaves({ extractionFields: inspectorExtractionFields, inspectorFlatFields })
        .length,
    [inspectorExtractionFields, inspectorFlatFields],
  );

  // Schema size, not the extracted count the expanded inspector's badge shows: the
  // extracted count needs this document's results, which only load once the panel opens.
  const inspectorTabCountByKey = {
    [InspectorTab.AI_FIELDS]: inspectorFieldLeafCount,
    [InspectorTab.LINKS]: inspectorLinkCount + getTaskLinkCount(currentDocument?.TaskId),
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    setContentWidth(containerRef.current.clientWidth);
  }, [containerRef, setContentWidth]);

  useEffect(() => {
    // Side-pane instances browse independently — they must not all jump to the page's shared
    // ?documentId= deep-link, so only the primary (non-embedded) instance honors it.
    if (isEmbeddedInPane || !documentIdSearchParam) {
      return;
    }

    setDocumentId(documentIdSearchParam);
  }, [documentIdSearchParam, isEmbeddedInPane]);

  useEffect(() => {
    if (!isDocumentIdChanged) {
      return;
    }

    setIsDocumentLoaded(false);
  }, [isDocumentIdChanged]);

  useEffectSafeStrictMode(() => {
    if (!loanApplicationId || !isAiDocumentIntakeAllowed) {
      return;
    }

    dispatch(initIntakeForLoanAction({ loanApplicationId }));
  }, [dispatch, loanApplicationId, isAiDocumentIntakeAllowed]);

  // Sole owner of the per-type definition load: the rail needs it while the inspector is
  // collapsed, and DocumentInspector resolves the same type id for its tabs. Guarded by the
  // same pair that gates the AI Fields tab, so surfaces that never show it stay silent.
  // Cached per type.
  useEffectSafeStrictMode(() => {
    const railDocumentTypeId = currentDocument?.documentTypeId;
    const isAiFieldsReachable = isExtractedFieldsViewAllowed && isExtractedFieldsViewNeeded;

    if (!railDocumentTypeId || !isAiFieldsReachable || inspectorFlatFields !== null) {
      return;
    }

    dispatch(
      getInspectorExtractionFieldsByDocTypeIdAction({ documentTypeId: railDocumentTypeId }),
    ).catch(err => {
      logger.error(err);
    });
  }, [
    dispatch,
    currentDocument?.documentTypeId,
    inspectorFlatFields,
    isExtractedFieldsViewAllowed,
    isExtractedFieldsViewNeeded,
  ]);

  const inspectorRailTabs = INSPECTOR_TABS_CONFIG.map(tab => ({
    ...INSPECTOR_RAIL_TABS.find(r => r.key === tab.value),
    active: inspectorActiveTab === tab.value,
    count: inspectorTabCountByKey[tab.value] ?? null,
  }));

  const treeView = (
    <DocumentTreeView
      isView
      documentId={documentId}
      isCopyDocumentsAllowed={isCopyDocumentsAllowed}
      isDataLoaded={isDataLoaded}
      isLoading={isLoading}
      loanParams={ownerParams}
      ref={documentTreeRef}
      setIsLoading={setIsLoading}
      onCollapsePanelLeft={isMobile ? null : onCollapseList}
      onCustomNavigateDocument={setDocumentId}
      onOpenAiBatchIntake={
        isDocumentsEditAllowed && isAiDocumentIntakeAllowed ? onOpenAiBatchIntake : undefined
      }
      onOpenDocumentItemContextMenu={isDocumentsEditAllowed ? onOpenContextMenu : undefined}
    />
  );

  const documentView = (
    <DocumentView
      isAiExtractionResultDisplayed
      isDownloadable
      isOpenInNewTabAllowed
      isPrintable
      isView
      afterSplitCb={setDocumentId}
      boundingBoxPopUpInfo={boundingBoxPopUpInfo}
      classes={viewerPaneRedesignInlineClasses}
      containerRef={containerRef}
      contentWidth={contentWidth}
      docId={documentId}
      isControlVisible={isDocumentViewControlVisible}
      isFocusMode={isFocusMode}
      isThumbnailsVisible={!isThumbnailsHidden}
      isViewHistoryAvailable={isDocumentsEditAllowed}
      loanParams={ownerParams}
      locatedFieldSource={locatedFieldSource}
      ref={documentViewRef}
      resetDocumentId={resetDocumentId}
      setBoundingBoxPopUpInfo={setBoundingBoxPopUpInfo}
      onDocumentLoaded={onDocumentLoaded}
      onDownloadDocument={onDownloadDocument}
      onOpenDocumentInNewTab={onOpenDocumentInNewTab}
      onPrintDocument={onPrintDocument}
      onToggleFocus={isMobile ? null : onToggleFocus}
    />
  );

  const gridSx = documentsRedesignLayoutStyles({ isListCollapsed, isInspectorCollapsed });

  return (
    <>
      <IntakeStatusCard loanApplicationId={loanApplicationId} onOpenReview={onOpenIntakeReview} />

      <Box
        className={clsx(documentWrapperClassName, 'flex-1 overflow-hidden')}
        ref={setRef}
        sx={gridSx}
      >
        {isListCollapsed && !isMobile ? (
          <CollapsedPaneRail
            count={null}
            iconName={DocumentIcon}
            label="Documents"
            side="left"
            onExpand={onExpandList}
          />
        ) : (
          treeView
        )}

        {documentView}

        {isInspectorCollapsed && !isMobile ? (
          <CollapsedPaneRail
            label="Inspector"
            side="right"
            tabs={inspectorRailTabs}
            onExpand={onExpandInspector}
            onExpandToTab={onExpandInspectorToTab}
          />
        ) : (
          <Box sx={inspectorStackingStyles}>
            <DocumentInspector
              activeTab={inspectorActiveTab}
              documentId={documentId}
              isExtractedFieldsViewNeeded={isExtractedFieldsViewNeeded}
              onCollapse={isMobile ? null : onCollapseInspector}
              onShowFieldSource={setLocatedFieldSource}
              onTabChange={setInspectorActiveTab}
            />
          </Box>
        )}
      </Box>

      <DrawerCDS
        content={<AiBatchIntake onComplete={onAiBatchIntakeComplete} />}
        dataTestId={LoanApplicationDocumentsTestIds.BATCH_INTAKE_DRAWER}
        open={isAiBatchIntakeOpen}
        size={DrawerSize.SMALL}
        title="AI Batch Intake"
        onClose={onCloseAiBatchIntake}
      />

      <DrawerCDS
        content={<AiBatchIntake job={reviewJob} onComplete={onIntakeReviewComplete} />}
        dataTestId={LoanApplicationDocumentsTestIds.REVIEW_INTAKE_DRAWER}
        open={!!reviewJob}
        size={DrawerSize.LARGE}
        title="Review Intake"
        onClose={onCloseIntakeReview}
      />

      {isDocumentsEditAllowed && !!anchorPosition && (
        <DocumentContextMenu
          anchorPosition={anchorPosition}
          document={currentDocument}
          isDocumentLoaded={isDocumentLoaded}
          isSplitDisabled={!!documentViewRef.current?.isDisabledSplitButton}
          onClose={onClearAnchorPosition}
          onMoveDocumentToType={onMoveDocumentToType}
          onOpenWorksheet={onOpenWorksheet}
          onRotateDocumentWithSave={onRotateDocumentWithSave}
          onSplitPages={onSplitPages}
        />
      )}

      {!!worksheetDrawerState && (
        <WorksheetDrawer
          applicationId={loanApplicationId}
          documentTypeId={worksheetDrawerState.documentTypeId}
          loanId={ownerParams?.loanId ?? null}
          open={!!worksheetDrawerState}
          worksheetDocumentId={worksheetDrawerState.worksheetDocumentId}
          onClose={onCloseWorksheet}
        />
      )}
    </>
  );
};

Documents.propTypes = {
  ...WithLoadingPropTypes,
  // One of loanParams / documentOwnerParams must be supplied. loanParams is kept
  // for the loan editor call sites; documentOwnerParams is the generic owner bag.
  loanParams: PropTypes.object,
  documentOwnerParams: PropTypes.object,
  pageCode: PropTypes.string,
  isDataLoaded: PropTypes.bool,
  documentWrapperClassName: PropTypes.string,
  isExtractedFieldsViewNeeded: PropTypes.bool,
  isCopyDocumentsAllowed: PropTypes.bool,
  isEmbeddedInPane: PropTypes.bool,
};

// Loan editor entry point — gated on the loan document page code (unchanged).
export default compose(withLoading, withPagePermissions(PAGE_CODES.DOCUMENTS), memo)(Documents);

// SubCompany entry point — same manager, gated on the subcompany document page
// code so loan-doc and subcompany-doc claims stay decoupled (DEV-3018).
export const SubCompanyDocuments = compose(
  withLoading,
  withPagePermissions(PAGE_CODES.SUB_COMPANIES_DOCUMENTS),
  memo,
)(Documents);
